import React, {useEffect, useRef, useState} from 'react';

import Head from 'next/head';

import {SprintGlobalStyle} from './sprintGlobalStyle';

/**
 * Stryd Speed launch / early-access landing page.
 *
 * Ported from the Claude Design project "Stryd Sprint.html". It is a
 * self-contained lander with its own minimal header and footer, so it is
 * rendered outside the standard site Layout. Media lives under
 * `public/images/sprint/` (see the filename list in the PR / README note).
 */

const asset = (name: string): string => `/images/sprint/${name}`;

// Autoplay muted-video ref helper — mirrors the design's inline behaviour so
// the loops start on mobile Safari (muted + playsInline + programmatic play).
const autoPlay = (el: HTMLVideoElement | null): void => {
  if (el) {
    el.muted = true;
    el.defaultMuted = true;
    el.volume = 0;
    el.play().catch(() => undefined);
  }
};

const EA_HREF = '#early-access';

const Arrow = () => (
  <svg
    viewBox="0 0 24 24"
    width="15"
    height="15"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const Check = () => (
  <svg
    viewBox="0 0 16 16"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2.5 8.5L6 12l7.5-8" />
  </svg>
);

/* ---------- header ---------- */
const SprintHead = ({homeHref}: {homeHref: string}) => (
  <header className="sp-head">
    <div className="sp-head-in">
      <a className="sp-brand" href={homeHref}>
        <img src={asset('stryd-speed-logo.svg')} alt="STRYD Speed" />
      </a>
      <a className="btn-ea sm" href={EA_HREF}>
        Get early access
      </a>
    </div>
  </header>
);

/* ---------- hero data overlay ----------
 * A live per-step chart mocked to sync with the 15s hero loop: athlete 1 for
 * 0–8s, athlete 2 for 8–15s, resetting at the switch and on each loop. X is
 * step count; both series (speed, ground contact time) are indexed to their
 * own session peak so they share one unlabeled Y — the real m/s / ms values
 * live in the readout, not on the axis. */
const HERO_TOTAL = 15; // seconds — full hero loop
const HERO_SPLIT = 8; // seconds — athlete 1 → athlete 2

interface StepDatum {
  step: number;
  t: number; // seconds into this athlete's segment
  speed: number; // m/s
  gct: number; // ground contact time, ms
}

// Mock one accelerating sprint: speed ramps to peak, contact time falls, and
// cadence climbs (steps land closer together) as the athlete gets faster.
const buildSteps = (cfg: {
  dur: number;
  startSpeed: number;
  peakSpeed: number;
  tau: number;
  gctStart: number;
  gctPeak: number;
  cadStart: number;
  cadPeak: number;
  seed: number;
}): StepDatum[] => {
  const out: StepDatum[] = [];
  let t = 0;
  let step = 0;
  while (t < cfg.dur && step < 80) {
    step += 1;
    const ramp = 1 - Math.exp(-t / cfg.tau);
    const speed = Math.max(
      0,
      cfg.startSpeed +
        (cfg.peakSpeed - cfg.startSpeed) * ramp +
        Math.sin(step * 1.9 + cfg.seed) * 0.06
    );
    const gct =
      cfg.gctPeak +
      (cfg.gctStart - cfg.gctPeak) * (1 - ramp) +
      Math.sin(step * 2.3 + cfg.seed) * 2;
    const cadence = cfg.cadStart + (cfg.cadPeak - cfg.cadStart) * ramp; // steps/sec
    out.push({step, t, speed, gct});
    t += 1 / cadence;
  }
  return out;
};

const ATHLETES = [
  {
    label: 'Athlete 1',
    steps: buildSteps({
      dur: HERO_SPLIT,
      startSpeed: 2.1,
      peakSpeed: 9.5,
      tau: 1.7,
      gctStart: 215,
      gctPeak: 98,
      cadStart: 3.0,
      cadPeak: 4.8,
      seed: 0,
    }),
  },
  {
    label: 'Athlete 2',
    steps: buildSteps({
      dur: HERO_TOTAL - HERO_SPLIT,
      startSpeed: 2.6,
      peakSpeed: 10.2,
      tau: 1.4,
      gctStart: 198,
      gctPeak: 90,
      cadStart: 3.2,
      cadPeak: 5.0,
      seed: 1.7,
    }),
  },
].map((a) => ({
  ...a,
  speedMax: Math.max(...a.steps.map((s) => s.speed)),
  gctMax: Math.max(...a.steps.map((s) => s.gct)),
}));

// Validated on a dark surface (dataviz skill): CVD ΔE ~31, in the lightness
// band, contrast ≥3:1. Speed = orange, ground contact = blue.
const C_SPEED = '#E8590C';
const C_GCT = '#4C8DFF';

const GW = 300;
const GH = 172;
const PAD = {l: 16, r: 14, t: 14, b: 34};

const clamp = (v: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, v));

// Kept intact but not currently rendered (the hero shows the pods image
// instead). Re-enable by uncommenting <HeroDataOverlay/> in SprintHero.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const HeroDataOverlay = ({
  videoRef,
}: {
  videoRef: React.RefObject<HTMLVideoElement>;
}) => {
  const [{phase, count}, setView] = useState({phase: 0, count: 0});
  const lastRef = useRef({phase: -1, count: -1});

  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const v = videoRef.current;
      if (v && !Number.isNaN(v.currentTime)) {
        const loop =
          v.duration && !Number.isNaN(v.duration) ? v.duration : HERO_TOTAL;
        const time = v.currentTime % loop;
        const nextPhase = time < HERO_SPLIT ? 0 : 1;
        const elapsed = nextPhase === 0 ? time : time - HERO_SPLIT;
        const {steps} = ATHLETES[nextPhase];
        let n = 0;
        while (n < steps.length && steps[n].t <= elapsed) n += 1;
        if (
          nextPhase !== lastRef.current.phase ||
          n !== lastRef.current.count
        ) {
          lastRef.current = {phase: nextPhase, count: n};
          setView({phase: nextPhase, count: n});
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [videoRef]);

  const athlete = ATHLETES[phase];
  const {steps} = athlete;
  const total = steps.length;
  const shown = steps.slice(0, count);
  const cur = shown[shown.length - 1];
  const lastIdx = shown.length - 1;

  const plotW = GW - PAD.l - PAD.r;
  const plotH = GH - PAD.t - PAD.b;
  const baseY = GH - PAD.b;
  const px = (i: number) =>
    PAD.l + (total <= 1 ? 0 : (i / (total - 1)) * plotW);
  const pySpeed = (s: number) =>
    PAD.t + plotH * (1 - clamp(s / athlete.speedMax, 0, 1));
  const pyGct = (g: number) =>
    PAD.t + plotH * (1 - clamp(g / athlete.gctMax, 0, 1));

  const buildPath = (fy: (d: StepDatum) => number) =>
    shown
      .map((d, i) => `${i ? 'L' : 'M'}${px(i).toFixed(1)} ${fy(d).toFixed(1)}`)
      .join(' ');
  const speedPath = buildPath((d) => pySpeed(d.speed));
  const gctPath = buildPath((d) => pyGct(d.gct));
  const speedArea =
    shown.length > 1
      ? `${speedPath} L${px(lastIdx).toFixed(1)} ${baseY} L${px(0).toFixed(
          1
        )} ${baseY} Z`
      : '';

  const ticks =
    total > 1 ? Array.from(new Set([1, Math.round(total / 2), total])) : [1];

  return (
    <div className="hero-graph" aria-hidden="true">
      <img
        className="fpod hg-pod"
        src={asset('stryd-sprint-hardware-off-clip.png')}
        alt=""
      />
      <svg className="hg-feed" viewBox="0 0 44 52" aria-hidden="true">
        <path className="hg-feed-line" d="M22 3 C22 20 22 24 22 39" />
        <path className="hg-feed-head" d="M13 32 L22 44 L31 32" />
      </svg>
      <div className="hg-head">
        <span className="hg-live">
          <i />
          LIVE
        </span>
        <span className="hg-athlete">{athlete.label}</span>
      </div>
      <div className="hg-read">
        <div className="hg-metric hg-speed">
          <span className="lbl">Speed</span>
          <span className="val">
            <b>{cur ? cur.speed.toFixed(2) : '—'}</b>
            <u>m/s</u>
          </span>
        </div>
        <div className="hg-metric hg-gct">
          <span className="lbl">Ground contact</span>
          <span className="val">
            <b>{cur ? Math.round(cur.gct) : '—'}</b>
            <u>ms</u>
          </span>
        </div>
      </div>
      <svg className="hg-chart" viewBox={`0 0 ${GW} ${GH}`}>
        <defs>
          <linearGradient id="hgSpeedFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C_SPEED} stopOpacity="0.34" />
            <stop offset="100%" stopColor={C_SPEED} stopOpacity="0" />
          </linearGradient>
        </defs>
        <line
          x1={PAD.l}
          y1={baseY}
          x2={GW - PAD.r}
          y2={baseY}
          stroke="rgba(255,255,255,0.22)"
          strokeWidth="1"
        />
        {ticks.map((tk) => {
          const xx = px(tk - 1);
          return (
            <g key={tk}>
              <line
                x1={xx}
                y1={baseY}
                x2={xx}
                y2={baseY + 4}
                stroke="rgba(255,255,255,0.28)"
                strokeWidth="1"
              />
              <text
                x={xx}
                y={baseY + 15}
                textAnchor="middle"
                className="hg-tick"
              >
                {tk}
              </text>
            </g>
          );
        })}
        <text x={PAD.l} y={GH - 4} className="hg-axis-title">
          STEP COUNT
        </text>
        {speedArea && <path d={speedArea} fill="url(#hgSpeedFill)" />}
        {gctPath && (
          <path
            d={gctPath}
            fill="none"
            stroke={C_GCT}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        {speedPath && (
          <path
            d={speedPath}
            fill="none"
            stroke={C_SPEED}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        {cur && (
          <>
            <g
              key={`g-${phase}`}
              className="hg-dot"
              style={{
                transform: `translate(${px(lastIdx).toFixed(1)}px, ${pyGct(
                  cur.gct
                ).toFixed(1)}px)`,
              }}
            >
              <circle r="3.2" fill={C_GCT} stroke="#0C0C0E" strokeWidth="1.2" />
            </g>
            <g
              key={`s-${phase}`}
              className="hg-dot"
              style={{
                transform: `translate(${px(lastIdx).toFixed(1)}px, ${pySpeed(
                  cur.speed
                ).toFixed(1)}px)`,
              }}
            >
              <circle
                r="3.2"
                fill={C_SPEED}
                stroke="#0C0C0E"
                strokeWidth="1.2"
              />
            </g>
          </>
        )}
      </svg>
      <div className="hg-legend">
        <span className="hg-lg hg-speed">
          <i />
          Speed
        </span>
        <span className="hg-lg hg-gct">
          <i />
          Ground contact
        </span>
      </div>
    </div>
  );
};

/* ---------- hero ---------- */
const SprintHero = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.muted = true;
      v.defaultMuted = true;
      v.volume = 0;
      v.play().catch(() => undefined);
    }
  }, []);
  return (
    <section className="sp-hero">
      <div className="sp-hero-media">
        <video
          ref={videoRef}
          src={asset('header-video-4.mp4')}
          autoPlay
          muted
          loop
          playsInline
        />
      </div>
      <div className="sp-hero-scrim" />
      <div className="sp-hero-in">
        <div className="sp-hero-copy">
          <div className="eyebrow">Introducing Stryd Speed</div>
          {/*<h1>Every step. Every rep. Measured.</h1>*/}
          {/*<h1>Speed is a skill. Now you can train it like one.</h1>*/}
          {/*<h1>You have only measured a fraction of your speed.</h1>*/}
          <h1>Unlock your speed with true 3D motion</h1>
          {/*<p className="sub">
            Speed, ground contact, air time, and stride mechanics — at every
            step, on every surface. Captured, cut into reps, and ready before
            your athlete is back on the line.
          </p>*/}
          <p className="sub">
            A timing gate catches one data point. A camera catches the strides
            in frame. Stryd Speed catches every step you take — speed, ground
            contact, air time, stride mechanics, both sides — recorded
            automatically, cut into reps, and analyzed in seconds.
          </p>
          <a className="btn-ea" href={EA_HREF}>
            Get early access <Arrow />
          </a>
          <div className="authority">
            <span className="dot" />
            Already in use with elite sprinters and pro athletes
          </div>
        </div>
        {/* Live data overlay temporarily swapped for the product image.
            HeroDataOverlay + its data model are kept intact below so it can be
            dropped back in by uncommenting this line. */}
        {/* <HeroDataOverlay videoRef={videoRef} /> */}
        <img
          className="hero-pods"
          src={asset('stryd-sprint-hardware-2-pods.png')}
          alt="Two Stryd Speed pods"
        />
      </div>
    </section>
  );
};

/* ---------- what it is ---------- */
// Metrics grouped into the three phases of a sprint they describe. Units and
// names track the Stryd Speed metric definitions.
const METRIC_CATS = [
  {
    title: 'Step-by-Step',
    blurb: 'Captured for every single step, left & right.',
    metrics: [
      {n: 'Velocity', u: 'm/s'},
      {n: 'Step rate', u: 'steps/s'},
      {n: 'Ground contact time', u: 'ms'},
      {n: 'Air time', u: 'ms'},
      {n: 'Stride length', u: 'm'},
      {n: 'Peak vertical ground reaction force', u: 'BW'},
      {n: 'Peak velocity', u: 'm/s'},
      {n: 'Impact velocity', u: 'm/s'},
      {n: 'Toe-off velocity', u: 'm/s'},
      {n: 'Footpath: 3D motion of every step', u: ''},
    ],
  },
  {
    title: 'Rep-by-Rep',
    blurb: 'Identify the best reps easily.',
    metrics: [
      {n: 'Peak velocity', u: 'm/s'},
      {n: 'Time to peak velocity', u: 'm/s'},
      {n: 'Step-by-step metrics (at peak velocity)', u: ''},
      {n: 'Start acceleration', u: 'm/s²'},
      {n: 'Average velocity', u: 'm/s'},
      {n: 'Duration', u: 'sec'},
      {n: 'Distance', u: 'm'},
    ],
  },
  {
    title: 'Sessions-by-Session',
    blurb: 'Understand the work load of every session.',
    metrics: [
      {n: 'Peak velocity (Best)', u: 'm/s'},
      {n: 'Start acceleration (best)', u: 'm/s²'},
      {n: 'Average velocity', u: 'm/s'},
      {n: 'Average start acceleration', u: 'm/s²'},
      {n: 'Repetition count', u: ''},
      {n: 'Duration (inc. active & recovery)', u: 'sec'},
      {n: 'Total distance', u: 'm'},
    ],
  },
];

const WhatIs = () => (
  <section className="sp">
    <div className="wrap">
      <div className="def-intro">
        <div className="def-copy">
          <div className="eyebrow">What it is</div>
          <h2 className="sp-h">
            A dual-sided footpod system that measures and captures your
            performance — including the way you move.
          </h2>
          <p className="sp-lede">
            Stryd Speed, a dual-sided pod system, clips to the shoes and logs
            every step on both feet — from the drive out of the blocks to the
            force of each landing. Metrics across every phase of the sprint, in
            real units, with no setup: recorded on-device and waiting in the
            portal before your athlete has finished cooling down.
          </p>
        </div>
        <div className="def-stage">
          <div className="def-photo">
            <img
              src={asset('stryd-the-footpod.png')}
              alt="The Stryd Speed footpod being clipped onto a running shoe at the track"
            />
          </div>
          <div className="def-clip">
            <video
              ref={autoPlay}
              src={asset('stryd-speed-clip-on.mp4')}
              autoPlay
              muted
              loop
              playsInline
              aria-label="Clipping the Stryd Speed pod onto a shoe"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- metrics ---------- */
const Metrics = () => (
  <section className="sp">
    <div className="wrap">
      <div className="eyebrow">What it measures</div>
      <h2 className="sp-h">Metrics for every step, repetition, and session.</h2>
      <p className="sp-lede">
        From the drive out of the blocks to the full 3D capture of your stride —
        grouped by the phase of the stride they describe, captured for every
        step on both feet, in real units.
      </p>
      <div className="metric-cats">
        {METRIC_CATS.map((cat, i) => (
          <div className="mcat" key={cat.title}>
            <div className="mcat-top">
              <span className="mcat-num mono">0{i + 1}</span>
              <h3 className="mcat-title">{cat.title}</h3>
              <p className="mcat-blurb">{cat.blurb}</p>
            </div>
            <ul className="mcat-list">
              {cat.metrics.map((m) => (
                <li key={m.n}>
                  <span className="m-name">{m.n}</span>
                  <span className="m-unit mono">{m.u}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="footpath">
        <div className="fp-copy">
          <div className="eyebrow">Footpath</div>
          <h3 className="fp-h">3D motion of every step.</h3>
          <p>
            The exact path the foot travels through each stride, traced in full
            3D — the mechanics coaches used to chase with high-speed cameras,
            now recorded automatically at every step of every rep.
          </p>
        </div>
        <div className="fp-media">
          <video
            ref={autoPlay}
            src={asset('footpath-accel.mp4')}
            autoPlay
            muted
            loop
            playsInline
            aria-label="3D footpath — the path the foot travels each stride, traced in full"
          />
        </div>
      </div>
    </div>
  </section>
);

/* ---------- how it works ---------- */
// Each step shows a looping video of that stage of the journey. `vid` reuses
// existing project footage for now — swap for the real clips later:
//   01 an actual clip-on demo · 02 an athlete running · 03 the app syncing ·
//   04 the dashboard populating.
// Each step takes either a `vid` (looping video) or an `img` — whichever is
// provided is rendered.
const HIW: {h: string; p: string; alt: string; vid?: string; img?: string}[] = [
  {
    h: 'Clip.',
    p: 'Stryd Speed clips to any shoe. Move and the pod starts recording instantly.',
    img: asset('stryd-speed-clip-it-on.png'),
    alt: 'Clipping the Stryd Speed pod onto a shoe',
  },
  {
    h: 'Train & Race.',
    p: 'Every rep, every step is tracked automatically — no gates, no start button, no babysitting. Use it realtime for instant feedback or just record for later.',
    vid: asset('take-off.mp4'),
    alt: 'Athlete running at speed wearing Stryd Speed',
  },
  {
    h: 'Sync.',
    p: 'Data flows off the pod through the mobile app when the session wraps.',
    vid: asset('stryd-speed-pod-sync.mp4'),
    alt: 'The mobile app syncing session data off the pod',
  },
  {
    h: 'Analyzed.',
    p: 'Organized into reps and analyzed the moment the workout ends. No waiting.',
    img: asset('instantly-analyzed.png'),
    alt: 'The portal dashboard populating with the finished session',
  },
];

const HowItWorks = () => (
  <section className="sp">
    <div className="wrap">
      <div className="eyebrow">How it works</div>
      <h2 className="sp-h">Clip. Train &amp; Race. Done.</h2>
      <div className="hiw-steps">
        {HIW.map((s, i) => (
          <div className="jstep" key={i}>
            <div className="jstep-media">
              {s.vid ? (
                <video
                  ref={autoPlay}
                  src={s.vid}
                  autoPlay
                  muted
                  loop
                  playsInline
                  aria-label={s.alt}
                />
              ) : (
                <img src={s.img} alt={s.alt} />
              )}
              <span className="jstep-num mono">0{i + 1}</span>
            </div>
            <h3>{s.h}</h3>
            <p>{s.p}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- live mode ---------- */
const LM_POINTS = [
  'Key metrics from every rep, the moment the athlete crosses',
  'Sync the full session to the portal on demand',
  'No timing gates to set up or read for results',
  'No start/stop video capture to run',
  'No cables or extra equipment to haul out',
];

const LiveMode = () => (
  <section className="sp">
    <div className="wrap">
      <div className="lm-grid">
        <div>
          <div className="eyebrow">Live mode</div>
          <h2 className="sp-h">
            Session progress, athlete performance, and 3D motion — all in real
            time.
          </h2>
          <p className="sp-lede">
            Bring a tablet trackside and watch every repetition land in real
            time. Peak speed, ground contact, air time — rep after rep, athlete
            after athlete — with nothing to set up and nothing to wait for. When
            the session wraps, sync it all to the portal on demand.
          </p>
          <ul className="lm-checks">
            {LM_POINTS.map((p) => (
              <li key={p}>
                <span className="ic">
                  <Check />
                </span>
                {p}
              </li>
            ))}
          </ul>
        </div>
        <div className="lm-stage">
          <img
            className="lm-photo"
            src={asset('live-mode.png')}
            alt="Stryd Speed live mode — trackside phone views"
          />
          <div className="lm-videowrap">
            <video
              className="lm-video"
              ref={autoPlay}
              src={asset('stryd-speed-coaching.mp4')}
              autoPlay
              muted
              loop
              playsInline
              aria-label="A coach reviewing a live session trackside"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- breakthrough ---------- */
// Each method takes either an `img` or a `vid` (bare filename under
// /images/sprint/) — whichever is provided is rendered.
const METHODS: {
  name: string;
  tag: string;
  old: string;
  shot: string;
  adv: string[];
  img?: string;
  vid?: string;
}[] = [
  {
    name: 'Timing Gates',
    tag: 'Replaces',
    old: 'One split per segment, fixed to wherever you dragged the tripods.',
    shot: 'Step-by-step velocity & biomechanics vs. a single gate split',
    vid: 'step-by-step.mp4',
    adv: [
      'Velocity at every step — not one split per segment',
      'Works anywhere on the field, no tripods to place',
      'Shows why an athlete got faster, not just that they did',
      'Captures what happens after the finish, including purposeful deceleration',
    ],
  },
  {
    name: 'Video Analysis',
    tag: 'Reduces',
    old: 'Hours of setup and frame-by-frame review, for the strides that happened in frame.',
    shot: 'Full-session mechanics, auto-cut — no film review',
    vid: 'footpath-animated.mp4',
    adv: [
      'Full-session mechanics — every stride, not just those in frame',
      'Zero setup, zero video burden',
      'Reps cleaned and organized the moment the session ends',
      'No junk data, precise, and accurate sprint information',
    ],
  },
  {
    name: 'GPS Trackers',
    tag: 'Exceeds',
    old: 'Great for volume and load. At sprint speed, the steps blur together.',
    shot: 'Get step-level detail. GPS blurs at sprint speed',
    vid: 'sprint-phases.mp4',
    adv: [
      'Step-level resolution at full sprint speed',
      "Ground contact, air time, and stride mechanics GPS can't see",
      'Works indoors — no satellite, no dropout, fully mobile anywhere',
      'Capture biomechanics of sprint movement, all in 3D',
    ],
  },
];

// Comparison visual for each method card (the `shot` text doubles as alt).
const MethodShot = ({
  img,
  vid,
  label,
}: {
  img?: string;
  vid?: string;
  label: string;
}) => (
  <figure className="method-shot">
    <div className="method-media">
      {vid ? (
        <video
          ref={autoPlay}
          src={asset(vid)}
          autoPlay
          muted
          loop
          playsInline
          aria-label={label}
        />
      ) : (
        <img src={img ? asset(img) : undefined} alt={label} />
      )}
    </div>
    <figcaption className="method-cap">{label}</figcaption>
  </figure>
);

const Breakthrough = () => (
  <section className="sp">
    <div className="wrap">
      <div className="eyebrow">The breakthrough</div>
      <h2 className="sp-h">
        Stryd Speed captures all athletes all the time. No setup required.
      </h2>
      <p className="sp-lede">
        Stryd Speed doesn&apos;t add another responsibility to the bag — it
        exceeds and replaces the tools you&apos;re juggling today. Here&apos;s
        the head-to-head.
      </p>
      <div className="methods">
        {METHODS.map((m, i) => (
          <div className="method" key={i}>
            <MethodShot img={m.img} vid={m.vid} label={m.shot} />
            <div className="method-body">
              <div className="m-head">
                <span className="m-tag mono">{m.tag}</span>
                <h3 className="m-name">{m.name}</h3>
              </div>
              {/*<div className="m-old">{m.old}</div>*/}
              <div className="m-why mono">Why Stryd Speed wins</div>
              <ul className="m-adv">
                {m.adv.map((a, j) => (
                  <li key={j}>
                    <span className="ic">
                      <Check />
                    </span>
                    {a}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
      {/*<div className="cost-line">
        No capture volume to build. No gates to lay out. No film to review.
        Train anywhere — track, turf, hallway — and measure everything, for a
        fraction of what a camera system costs.
      </div>*/}
    </div>
  </section>
);

/* ---------- field band ---------- */
const FieldBand = () => (
  <section className="band">
    <video
      ref={autoPlay}
      src={asset('stryd-sprint-dead-start.mp4')}
      autoPlay
      muted
      loop
      playsInline
    />
    <div className="scrim" />
    {/*<div className="fchip" style={{right: '10%', top: '18%'}}>
      <span className="l">Rate of accel</span>
      <span className="v">
        4.8<em>m/s²</em>
      </span>
    </div>
    <div
      className="fchip alt"
      style={{right: '20%', top: '32%', animationDelay: '-3s'}}
    >
      <span className="l">Time to peak</span>
      <span className="v">
        3.9<em>s</em>
      </span>
    </div>*/}
    <div className="copy">
      <h2>
        No gates, cameras, or tripods,
        <br />
        and no more headaches.
      </h2>
      <p className="bs">
        The measurement follows the athlete — not the other way around.
      </p>
    </div>
  </section>
);

/* ---------- performance trend ---------- */
const PERF_SHOTS = [
  {
    src: 'Screenshot1.png',
    alt: 'Athlete dashboard — peak performances and activities list',
    cap: 'Each session summarized automatically.',
  },
  {
    src: 'Screenshot2.png',
    alt: 'Peak values across all reps — best of each',
    cap: 'Best of each rep, surfaced automatically for easy summary and comparison.',
  },
  {
    src: 'Screenshot3.png',
    alt: 'Peak-aligned rep comparison overlay',
    cap: 'Catch fatigue when it sets in — compare reps across time.',
  },
];

// Full-bleed, one-at-a-time screenshot slider (arrows, dots, swipe) so the
// portal views read large without stacking down the page.
const PlayIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="12"
    height="12"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M8 5v14l11-7z" />
  </svg>
);
const PauseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="12"
    height="12"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M7 5h3.2v14H7zM13.8 5H17v14h-3.2z" />
  </svg>
);

const ShotSlider = ({
  shots,
}: {
  shots: {src: string; alt: string; cap: string; tag?: string}[];
}) => {
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const n = shots.length;
  const go = (d: number) => setI((p) => (p + d + n) % n);

  // Auto-advance while playing; each index change restarts the timer.
  useEffect(() => {
    if (!playing) return undefined;
    const id = window.setTimeout(() => setI((p) => (p + 1) % n), 5000);
    return () => window.clearTimeout(id);
  }, [i, playing, n]);

  const startX = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (startX.current === null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    startX.current = null;
  };

  return (
    <div className="perf-slider">
      <div
        className="ps-viewport"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="ps-track"
          style={{transform: `translateX(-${i * 100}%)`}}
        >
          {shots.map((s, idx) => (
            <div
              className={`ps-slide ${idx === i ? 'is-active' : ''}`}
              key={s.src}
            >
              <figure className="ps-frame">
                <img src={asset(s.src)} alt={s.alt} loading="lazy" />
                <div className="ps-scrim" />
                <figcaption className="ps-cap">
                  <span className="t">{s.tag ?? 'Portal'}</span>
                  <p>{s.cap}</p>
                </figcaption>
              </figure>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="ps-arrow ps-prev"
          onClick={() => go(-1)}
          aria-label="Previous screenshot"
        >
          <svg
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
        <button
          type="button"
          className="ps-arrow ps-next"
          onClick={() => go(1)}
          aria-label="Next screenshot"
        >
          <svg
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      <div className="ps-controls">
        <div className="ps-pill">
          <div className="ps-dots">
            {shots.map((s, idx) => (
              <button
                type="button"
                key={s.src}
                className={`ps-dot ${idx === i ? 'on' : ''}`}
                onClick={() => setI(idx)}
                aria-label={`Show screenshot ${idx + 1}`}
                aria-current={idx === i}
              />
            ))}
          </div>
          <button
            type="button"
            className="ps-play"
            onClick={() => setPlaying((p) => !p)}
            aria-label={playing ? 'Pause slideshow' : 'Play slideshow'}
          >
            {playing ? <PauseIcon /> : <PlayIcon />}
          </button>
        </div>
      </div>
    </div>
  );
};

const Trend = () => (
  <section className="sp">
    <div className="wrap">
      <div className="eyebrow">Performance</div>
      <h2 className="sp-h">
        Training progress you can see and rely on, not guess.
      </h2>
      <p className="sp-lede">
        When every effort is captured, the trends do the paperwork: peak
        velocity, rate of acceleration, time to peak, mean velocity — session
        over session, athlete by athlete. No data entry. No spreadsheet nights.
        The record builds itself while you coach.
      </p>
    </div>
    <ShotSlider shots={PERF_SHOTS} />
    <div className="wrap">
      <div className="micro mono">
        <span>Rep auto-detection</span>
        <span>Rep tagging</span>
        <span>Session comparison</span>
        <span>
          Full-session recording — the run and the rest, not just splits
        </span>
      </div>
    </div>
  </section>
);

/* ---------- step-level data ---------- */
const STEP_SHOTS = [
  {
    tag: 'Insights for optimization',
    src: 'Screenshot4.png',
    alt: 'Step-by-step table with speed, GCT, air time, and stride',
    cap: 'Uncover your opportunities — both sides, quantified to the millisecond.',
  },
  {
    tag: 'Stryd footpath',
    src: 'Screenshot5.png',
    alt: 'Rep view with sagittal footpath — foot motion traced left and right',
    cap: '3D motion of every step, traced left and right — no high-speed cameras required.',
  },
  {
    tag: 'Metrics that matter',
    src: 'Screenshot6.png',
    alt: 'Learn what happens at every strike',
    cap: 'Understand the components of your stride that unlock your speed.',
  },
];

const StepLevel = () => (
  <section className="sp">
    <div className="wrap">
      <div className="eyebrow">The data underneath</div>
      <h2 className="sp-h">
        Dual-sided by design.
        <br />
        Imbalances show up as numbers, not hunches.
      </h2>
      <p className="sp-lede">
        Stryd Speed is a dual-sided system: a pod on each foot, capturing left
        and right independently and pairing every step automatically. Symmetry
        isn&apos;t a lab test anymore — it&apos;s data from every session. And
        when an athlete starts to break down, you&apos;ll see the exact step
        where it happened, quantified to the millisecond.
      </p>
    </div>
    <ShotSlider shots={STEP_SHOTS} />
  </section>
);

/* ---------- validation ----------
 * Reads as a validation summary, not a marketing claim. The proof points (the
 * claims) and the laser charts (the evidence) are decoupled: a clickable
 * accordion of claims on the left, an independent chart carousel on the right.
 * Register: a methods section translated for coaches — no superlatives. */
const VALIDATION_POINTS = [
  {
    title: 'Validated against the instruments researchers already trust.',
    body: 'Stryd Speed’s data matches the criterion set by gold-standard laboratory measurements.',
  },
  {
    title: 'Accurate across athletes — from youth to professionals.',
    body: 'Validated across body sizes, speeds, and running styles, from developing athletes to elite athletes. ',
  },
  {
    title: 'Accurate with no calibration required.',
    body: 'Accurate out of the box. No tech burden. Clip it on and it’s right, for every athlete on the roster, from the first step.',
  },
  {
    title: 'Accurate in every step of training and on race day.',
    body: 'Stryd Speed works for all athlete efforts with the same level of precision from a warmup of an easy session to race day when precision really matters.',
  },
  {
    title: 'Accurate from track to turf, and more.',
    body: 'Stryd Speed works the same everywhere. Track, turf, and grass — indoors or out. No GPS dependency means no degradation under a roof, in a hallway, or in a stadium bowl.',
  },
  {
    title: 'Repeatable accuracy, precision, and reliability.',
    body: 'The same effort produces the same number, session after session. When a trend appears in the portal, it’s the athlete changing, not the sensor.',
  },
  {
    title:
      'The measurement that follows the athlete. Not the other way around.',
    body: 'Stryd Speed agrees with gold-standard laboratory measurements — and then goes where no reference system can follow.',
  },
];

const VALIDATION_CHARTS = [
  {
    src: 'stryd-vs-laser-1-082526.png',
    alt: 'Stryd Speed step velocity overlaid on LASER reference velocity',
  },
  {
    src: 'stryd-vs-laser-4.png',
    alt: 'Agreement with the laser on track, turf, and grass',
  },
  {
    src: 'stryd-vs-laser-2-082526.png',
    alt: 'Agreement with the laser on track, turf, and grass',
  },
  {
    src: 'stryd-vs-laser-2.png',
    alt: 'Stryd Speed tracking the laser through the acceleration phase to max velocity',
  },
  // {
  //   src: 'stryd-vs-laser-3.png',
  //   alt: 'Agreement with the laser across athletes of different sizes and speeds',
  // },
];

// Reusable manual carousel — no copy overlaid; arrows/dots/swipe/counter sit
// below the image so nothing is covered. Independent of any adjacent accordion.
const MediaCarousel = ({slides}: {slides: {src: string; alt: string}[]}) => {
  const [i, setI] = useState(0);
  const n = slides.length;
  const go = (d: number) => setI((p) => (p + d + n) % n);
  const startX = useRef<number | null>(null);
  return (
    <div className="vc">
      <div
        className="vc-viewport"
        onTouchStart={(e) => {
          startX.current = e.touches[0].clientX;
        }}
        onTouchEnd={(e) => {
          if (startX.current === null) return;
          const dx = e.changedTouches[0].clientX - startX.current;
          if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
          startX.current = null;
        }}
      >
        <div
          className="vc-track"
          style={{transform: `translateX(-${i * 100}%)`}}
        >
          {slides.map((c) => (
            <div className="vc-slide" key={c.src}>
              <img src={asset(c.src)} alt={c.alt} loading="lazy" />
            </div>
          ))}
        </div>
      </div>
      <div className="vc-foot">
        <button
          type="button"
          className="vc-arrow"
          onClick={() => go(-1)}
          aria-label="Previous slide"
        >
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
        <div className="vc-dots">
          {slides.map((c, idx) => (
            <button
              type="button"
              key={c.src}
              className={`vc-dot ${idx === i ? 'on' : ''}`}
              onClick={() => setI(idx)}
              aria-label={`Show slide ${idx + 1}`}
              aria-current={idx === i}
            />
          ))}
        </div>
        <span className="vc-count mono">
          {i + 1} / {n}
        </span>
        <button
          type="button"
          className="vc-arrow"
          onClick={() => go(1)}
          aria-label="Next slide"
        >
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const Validation = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="sp">
      <div className="wrap">
        <div className="eyebrow">Validation</div>
        <h2 className="sp-h">
          Research-grade validation. Accurate from start to finish of every rep.
        </h2>
        <p className="sp-lede">
          Stryd Speed has been validated against gold-standard laboratory
          measurements. Precision at the millisecond level, Stryd Speed
          accurately captures the essential data for all athletes in all
          conditions.
        </p>
        <div className="val-grid">
          <div className="val-col">
            <div className="val-col-title">What the validation shows</div>
            <div className="val-points">
              {VALIDATION_POINTS.map((p, idx) => (
                <div
                  className={`val-point ${open === idx ? 'open' : ''}`}
                  key={p.title}
                >
                  <button
                    type="button"
                    className="val-q"
                    onClick={() => setOpen(open === idx ? -1 : idx)}
                    aria-expanded={open === idx}
                  >
                    <span className="val-marker mono">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <span className="val-title">{p.title}</span>
                    <svg
                      className="val-chev"
                      viewBox="0 0 16 16"
                      width="15"
                      height="15"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    >
                      <path d="M8 2v12M2 8h12" />
                    </svg>
                  </button>
                  <div className="val-a">
                    <div>
                      <p>{p.body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="val-col val-col-evidence">
            <div className="val-col-title">Stryd Speed vs. the Laser</div>
            <div className="val-evidence">
              <MediaCarousel slides={VALIDATION_CHARTS} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- contexts ---------- */
const CONTEXTS = [
  {
    h: 'Athlete Performance',
    p: 'Testing day, every day. Assessments and speed sessions with zero setup — standardized numbers when you need them, continuous numbers in between.',
    img: 'stryd-sprinting-on-track.png',
    alt: 'Sprinter driving out of the start on the track',
  },
  {
    h: 'Sports Science',
    p: 'Measurement that goes where sport happens: turf, grass, court, track, with no equipment fencing in the drill.',
    img: 'stryd-sprinting-on-turf.png',
    alt: 'Athlete accelerating on turf wearing Stryd Speed',
  },
  {
    h: 'Sports Medicine',
    p: 'Return-to-play you can measure: L/R contact symmetry, air time, stride length, and velocity progression, session by session. Watch dysfunctional asymmetry resolve — or catch it early. Data over feel.',
    vid: 'stryd-sprint-stretching.mp4',
    alt: 'Athlete stretching at the track',
  },
];

const Contexts = () => (
  <section className="sp">
    <div className="wrap">
      <div className="eyebrow">Every context</div>
      <h2 className="sp-h">One System: Extensive Uses</h2>
      <div className="ctx-rows">
        {CONTEXTS.map((c, i) => (
          <div className={`ctx ${i % 2 ? 'rev' : ''}`} key={i}>
            <div className="ph">
              {c.vid ? (
                <video
                  ref={autoPlay}
                  src={asset(c.vid)}
                  autoPlay
                  muted
                  loop
                  playsInline
                  aria-label={c.alt}
                />
              ) : (
                <img src={asset(c.img as string)} alt={c.alt} />
              )}
            </div>
            <div className="bd">
              <div className="ctx-num mono">0{i + 1}</div>
              <h3>{c.h}</h3>
              <p>{c.p}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- trusted ---------- */
const Trusted = () => (
  <section className="sp trusted">
    <div className="wrap">
      <div className="trusted-grid">
        <div>
          <div className="eyebrow">Authority</div>
          <h2 className="sp-h">Already on the fastest feet in sport.</h2>
          <p className="sp-lede">
            From track &amp; field to soccer, football, lacrosse, and basketball
            — across every turf and team sport — Stryd Speed measures the
            athletes who win on speed. And it fits every level of the game:
            youth development to the professional ranks, elite strength &amp;
            conditioning to rehab and return-to-play. Fast is fast everywhere —
            Stryd is built for all of it.
          </p>
          <div className="orgs">
            <span>Track &amp; field</span>
            <span>Soccer</span>
            <span>Football</span>
            <span>Lacrosse</span>
            <span>Basketball</span>
            <span>Turf and team sports</span>
            <span>Youth to pro</span>
            <span>Strength and conditioning</span>
            <span>Rehab and return-to-play</span>
            <span>Private facilities and clinics</span>
          </div>
          {/*<div className="tq">
            <blockquote>
              It&apos;s on every athlete, every session. We stopped scheduling
              testing days — the testing happens while we train.
            </blockquote>
            <div className="who">
              Head of performance, NFL club — quote placeholder pending
              permission
            </div>
          </div>*/}
        </div>
        <div className="trusted-photo">
          <img
            src={asset('stryd-on-blocks.png')}
            alt="Athlete stretching at the hurdle wearing Stryd Speed"
          />
        </div>
      </div>
    </div>
  </section>
);

/* ---------- motion band ---------- */
const MotionBand = () => (
  <section className="band">
    <video
      ref={autoPlay}
      src={asset('take-off.mp4')}
      autoPlay
      muted
      loop
      playsInline
    />
    <div className="scrim" />
    <div className="copy">
      <h2>It rides along. It writes everything down.</h2>
      <p className="bs">
        Stryd attaches to the shoe — while the athlete trains and competes.
      </p>
    </div>
  </section>
);

/* ---------- reliable ---------- */
// Coupled selector: each claim carries its own media — an image (`src`) or a
// looping `vid` (bare filename under /images/sprint/). The selected (active)
// item drives which media shows, and the selection auto-advances through the
// list; clicking an item takes over and restarts the timer.
const REL: {
  k: string;
  title: string;
  body: string;
  alt: string;
  src?: string;
  vid?: string;
}[] = [
  {
    k: 'SETUP',
    title: 'Clips on with a click.',
    body: 'Status lights say ready before the rep — not after.',
    src: 'stryd-the-footpod.png',
    alt: 'The Stryd Speed pod clipped onto the shoe, ready to record',
  },
  {
    k: 'SQUAD',
    title: 'One app runs your whole team.',
    body: 'The Stryd iPhone/iPad app manages every athlete on the roster — individuals and groups — with each pod recording independently.',
    vid: 'stryd-speed-coaching.mp4',
    alt: 'The Stryd Speed app — every athlete’s sessions in one roster',
  },
  {
    k: 'OFFLINE',
    title: 'Phone nearby, or no screens at all.',
    body: 'Onboard storage catches the whole session. No beacons, no GPS dependency, no gates, no limits.',
    vid: 'stryd-speed-pod-sync.mp4',
    alt: 'The dual-sided pair — one pod per shoe',
  },
  {
    k: 'BATTERY',
    title: 'Battery that outlasts the training week.',
    body: 'Charge it with the rest of your kit — at a per-athlete cost no camera system can touch.',
    src: 'stryd-sprinting-on-track.png',
    alt: 'Exploded view of the Stryd Speed pod',
  },
  {
    k: 'LIVE',
    title: 'A live display that soothes instead of floods.',
    body: 'The reference points you need mid-session, with drill-down when you want it.',
    vid: 'step-by-step.mp4',
    alt: 'Live session view — the reference points you need mid-session',
  },
];

const REL_INTERVAL = 5000; // ms per item before auto-advancing

const Reliable = () => {
  const [active, setActive] = useState(0);

  // Auto-advance; changing `active` (auto or via click) restarts the timer.
  useEffect(() => {
    const id = window.setTimeout(
      () => setActive((a) => (a + 1) % REL.length),
      REL_INTERVAL
    );
    return () => window.clearTimeout(id);
  }, [active]);

  return (
    <section className="sp">
      <div className="wrap">
        <div className="eyebrow">Engineered to just work</div>
        <h2 className="sp-h">
          Simplicity and reliability makes Stryd Speed your new favorite tool.
        </h2>
        <div className="val-grid">
          <div className="val-col">
            <div className="val-col-title">Why it just works</div>
            <div className="val-points">
              {REL.map((r, idx) => (
                <div
                  className={`val-point ${active === idx ? 'open' : ''}`}
                  key={r.k}
                >
                  <button
                    type="button"
                    className="val-q"
                    onClick={() => setActive(idx)}
                    aria-expanded={active === idx}
                  >
                    <span className="val-marker mono">{r.k}</span>
                    <span className="val-title">{r.title}</span>
                  </button>
                  <div className="val-a">
                    <div>
                      <p>{r.body}</p>
                    </div>
                  </div>
                  {active === idx && (
                    <span
                      className="val-progress"
                      key={active}
                      style={{animationDuration: `${REL_INTERVAL}ms`}}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="val-col val-col-evidence">
            <div className="val-col-title">Hardware &amp; app</div>
            <div className="val-evidence">
              <div className="rel-stage">
                {REL.map((r, idx) =>
                  r.vid ? (
                    <video
                      key={r.k}
                      ref={autoPlay}
                      className={`rel-img ${active === idx ? 'on' : ''}`}
                      src={asset(r.vid)}
                      autoPlay
                      muted
                      loop
                      playsInline
                      aria-label={r.alt}
                      aria-hidden={active !== idx}
                    />
                  ) : (
                    <img
                      key={r.k}
                      className={`rel-img ${active === idx ? 'on' : ''}`}
                      src={r.src ? asset(r.src) : undefined}
                      alt={r.alt}
                      aria-hidden={active !== idx}
                    />
                  )
                )}
              </div>
              <div className="rel-pips">
                {REL.map((r, idx) => (
                  <button
                    type="button"
                    key={r.k}
                    className={`vc-dot ${active === idx ? 'on' : ''}`}
                    onClick={() => setActive(idx)}
                    aria-label={`Show ${r.k}`}
                    aria-current={active === idx}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- own ---------- */
const Own = () => (
  <section className="sp">
    <div className="wrap">
      <div className="eyebrow">What you get</div>
      <h2 className="sp-h">
        Simple, transparent pricing that fits any organization.
      </h2>
      <div className="own-grid">
        <div className="own">
          <div className="own-media pod">
            <img
              src={asset('stryd-sprint-hardware-2-pods.png')}
              alt="Two Stryd Speed pods"
            />
          </div>
          <div className="own-body">
            <div className="o-k">Hardware</div>
            <h3>The pods: yours to keep.</h3>
            <p>
              Buy them, keep them. No per-session fees. No capture volume
              limits. No fine print.
            </p>
          </div>
        </div>
        <div className="own">
          <div className="own-media shot">
            <img
              src={asset('data-summary.png')}
              alt="The Stryd portal — full-session speed development view"
            />
          </div>
          <div className="own-body">
            <div className="o-k">Software</div>
            <h3>The portal: enabled by your subscription.</h3>
            <p>
              Analysis, comparisons, data trending — and a product that keeps
              getting better.
            </p>
          </div>
        </div>
      </div>
      <p className="price-note">
        Hardware and subscription pricing announced at launch. The early-access
        list hears first.
      </p>
    </div>
  </section>
);

/* ---------- shoe band ---------- */
const ShoeBand = () => (
  <section className="band">
    <img
      src={asset('stryd-on-spikes-2.png')}
      alt="Clipping Stryd Speed onto a track shoe"
      style={{objectPosition: 'center 38%'}}
    />
    <div className="scrim" />
    <div className="copy">
      <h2>Clip on and go. Lights confirm.</h2>
      <p className="bs">Set up in the time it takes to tie the other shoe.</p>
    </div>
  </section>
);

/* ---------- faq ---------- */
const FAQS = [
  {
    q: 'How do I start a session?',
    a: 'Simply place Stryd on your shoe and run. The pods detect your movements, and automatically form a system to record all of your steps. Blinking lights on the pod confirm recording.',
  },
  {
    q: 'How does the data get off the pod?',
    a: 'After the session, the mobile app syncs all of your data from all athlete pods. The pod also records device-free to onboard storage, so athletes can train with no screens around at all.',
  },
  {
    q: 'Can I see data in real time?',
    a: 'Yes. During a session, the mobile app displays data from all athletes, organized rep-by-rep, including session bests.',
  },
  {
    q: 'Do I need gates or cameras?',
    a: "No. The pod clips to the shoe and the measurement follows the athlete — there's nothing to lay out, calibrate, or film. Track, turf, grass, court, hallway, and more.",
  },
  {
    q: 'How long until results are ready?',
    a: 'Reps are detected, segmented, and analyzed the moment the workout ends. No film review, no processing queue — no waiting.',
  },
  {
    q: 'How many athletes can I run at once?',
    a: 'A full squad from one iPhone or iPad. The app manages your whole roster — individual athletes and groups — with each pod recording independently.',
  },
  {
    q: 'How often does it need charging?',
    a: 'The battery outlasts the training week. Charge it with the rest of your kit, not in the middle of a session.',
  },
  {
    q: 'What does it cost?',
    a: 'You buy the pods and keep them; the analysis portal is a subscription. Pricing is announced at launch — the early-access list hears first.',
  },
];

const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="sp">
      <div className="wrap">
        <div className="eyebrow">FAQ</div>
        <h2 className="sp-h">Questions, answered.</h2>
        <div className="faq-wrap">
          {FAQS.map((f, i) => (
            <div className={`faq-item ${open === i ? 'open' : ''}`} key={i}>
              <button
                className="faq-q"
                onClick={() => setOpen(open === i ? -1 : i)}
              >
                <span>{f.q}</span>
                <svg
                  viewBox="0 0 16 16"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                >
                  <path d="M8 2v12M2 8h12" />
                </svg>
              </button>
              <div className="faq-a">
                <div>
                  <p>{f.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- early access form ---------- */
// {slug, label} — slugs are what land in Klaviyo; labels are shown in the form.
const ROLE_OPTS = [
  {slug: 'coach', label: 'Coach'},
  {slug: 's_and_c', label: 'Strength & conditioning specialist'},
  {slug: 'athlete', label: 'Athlete'},
  {slug: 'rehab_return_to_play', label: 'Rehab / return to play specialist'},
  {slug: 'other', label: 'Other'},
];

const SPORT_OPTS = [
  {slug: 'track_and_field', label: 'Track & field'},
  {slug: 'soccer', label: 'Soccer'},
  {slug: 'football', label: 'Football'},
  {slug: 'lacrosse', label: 'Lacrosse'},
  {slug: 'basketball', label: 'Basketball'},
  {slug: 'other', label: 'Other'},
];

// Klaviyo client-side subscribe. `company_id` is the public key (safe in the
// browser). The early-access list is single opt-in, so SUBSCRIBED marketing
// consent takes effect immediately — no confirmation email.
const KLAVIYO_COMPANY_ID = 'RcYWM2';
const KLAVIYO_LIST_ID = 'R3zWSp';

const EarlyAccess = () => {
  const [roles, setRoles] = useState<string[]>([]);
  const [sports, setSports] = useState<string[]>([]);
  const [other, setOther] = useState('');
  const [sportOther, setSportOther] = useState('');
  const [email, setEmail] = useState('');
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const toggleIn = (
    slug: string,
    list: string[],
    setList: (v: string[]) => void
  ) =>
    setList(
      list.includes(slug) ? list.filter((x) => x !== slug) : [...list, slug]
    );
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErr('Enter a valid email address.');
      return;
    }
    setErr('');
    setSubmitting(true);
    const properties: Record<string, unknown> = {};
    if (roles.length) properties.role = roles;
    if (roles.includes('other') && other.trim()) {
      properties.role_other = other.trim();
    }
    if (sports.length) properties.sport = sports;
    if (sports.includes('other') && sportOther.trim()) {
      properties.sport_other = sportOther.trim();
    }
    try {
      const res = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`,
        {
          method: 'POST',
          headers: {'Content-Type': 'application/json', revision: '2026-07-15'},
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Stryd Speed early access',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      subscriptions: {
                        email: {marketing: {consent: 'SUBSCRIBED'}},
                      },
                      ...(Object.keys(properties).length ? {properties} : {}),
                    },
                  },
                },
              },
              relationships: {
                list: {data: {type: 'list', id: KLAVIYO_LIST_ID}},
              },
            },
          }),
        }
      );
      if (!res.ok) throw new Error(`Klaviyo responded ${res.status}`);
      setDone(true);
    } catch {
      setErr('Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <section className="ea" id="early-access">
      <div className="ea-in">
        <img
          className="ea-pod"
          src={asset('stryd-sprint-hardware-off-clip.png')}
          alt="Stryd Speed pod and clip"
        />
        <h2>Be the first to use Stryd Speed</h2>
        <p className="sub">
          Join the early-access list for news on pricing, availability, and a
          first look at the portal.
        </p>
        <div className="ea-form">
          {done ? (
            <div className="ea-success">
              <div className="ck">
                <svg
                  viewBox="0 0 16 16"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="#FF9015"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 8.5L6.5 12l6.5-8" />
                </svg>
              </div>
              <h3>You&apos;re on the list.</h3>
              <p>We&apos;ll be in touch before launch.</p>
            </div>
          ) : (
            <form onSubmit={(e) => void submit(e)} noValidate>
              <div className="fld">
                <label className="fl" htmlFor="ea-email">
                  Email address
                </label>
                <input
                  id="ea-email"
                  type="email"
                  placeholder="you@team.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                {err && <div className="err">{err}</div>}
              </div>
              <div className="fld">
                <div className="fl">I am a…</div>
                <div className="roles">
                  {ROLE_OPTS.map((o) => (
                    <label
                      className={`role ${roles.includes(o.slug) ? 'on' : ''}`}
                      key={o.slug}
                    >
                      <input
                        type="checkbox"
                        checked={roles.includes(o.slug)}
                        onChange={() => toggleIn(o.slug, roles, setRoles)}
                      />
                      <span>{o.label}</span>
                    </label>
                  ))}
                </div>
                {roles.includes('other') && (
                  <input
                    type="text"
                    style={{marginTop: 8}}
                    placeholder="Tell us your role"
                    value={other}
                    onChange={(e) => setOther(e.target.value)}
                  />
                )}
              </div>
              <div className="fld">
                <div className="fl">Sport</div>
                <div className="roles">
                  {SPORT_OPTS.map((o) => (
                    <label
                      className={`role ${sports.includes(o.slug) ? 'on' : ''}`}
                      key={o.slug}
                    >
                      <input
                        type="checkbox"
                        checked={sports.includes(o.slug)}
                        onChange={() => toggleIn(o.slug, sports, setSports)}
                      />
                      <span>{o.label}</span>
                    </label>
                  ))}
                </div>
                {sports.includes('other') && (
                  <input
                    type="text"
                    style={{marginTop: 8}}
                    placeholder="Tell us your sport"
                    value={sportOther}
                    onChange={(e) => setSportOther(e.target.value)}
                  />
                )}
              </div>
              <button className="btn-ea" type="submit" disabled={submitting}>
                {submitting ? (
                  'Joining…'
                ) : (
                  <>
                    Get early access <Arrow />
                  </>
                )}
              </button>
              <div className="privacy">
                Launch updates only. No spam, unsubscribe anytime.
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

/* ---------- footer ---------- */
const SprintFoot = ({homeHref}: {homeHref: string}) => (
  <footer className="sp-foot">
    <div className="sp-foot-in">
      <a className="sp-brand" href={homeHref}>
        <img
          src={asset('stryd-speed-logo.svg')}
          alt="STRYD Speed"
          style={{height: 13}}
        />
      </a>
      <div className="links">
        <a href={homeHref}>← Stryd.com</a>
        <a href="mailto:stryd@stryd.com">stryd@stryd.com</a>
        <a href="/privacy">Privacy</a>
        <a href="/terms-of-use">Terms</a>
        <a href="https://www.instagram.com/strydspeed">Instagram</a>
        <a href="https://www.youtube.com/c/Stryd">YouTube</a>
      </div>
      <span>© 2026 Stryd, Inc. Boulder, Colorado.</span>
    </div>
  </footer>
);

export interface SprintProps {
  /** Where the header/footer brand logo links to (region/locale home). */
  homeHref: string;
}

export const Sprint = ({homeHref}: SprintProps): JSX.Element => (
  <div className="sprint">
    <Head>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        rel="preconnect"
        href="https://fonts.gstatic.com"
        crossOrigin="anonymous"
      />
      <link
        href="https://fonts.googleapis.com/css2?family=Geist:wght@300;400;450;500;600;650;700&family=Geist+Mono:wght@400;500&display=swap"
        rel="stylesheet"
      />
    </Head>
    <SprintGlobalStyle />
    <SprintHead homeHref={homeHref} />
    <SprintHero />
    <WhatIs />
    <Metrics />
    <HowItWorks />
    <ShoeBand />
    <LiveMode />
    <Validation />
    <FieldBand />
    <Breakthrough />
    <Trend />
    <StepLevel />
    <Contexts />
    <Trusted />
    <MotionBand />
    <Reliable />
    <Own />
    <Faq />
    <EarlyAccess />
    <SprintFoot homeHref={homeHref} />
  </div>
);

export default Sprint;
